import { FocusEventHandler, KeyboardEventHandler, MouseEventHandler, PropsWithChildren, ReactNode, RefObject } from "react";

import { OWDataType } from "@multiweb/types/contracts";
import {
    NoneOwnProps,
    OnChangeHandler,
} from "@multiweb/types/react";
import { Cursor } from "../../../Global/Base/Enums";
import { ControlReduxRawValue, ControlsKey } from "../controls/types";
import { IExplicitValidationError } from "../controls/validation/types";
import { CaptionAlignment } from "./CheckBoxComponent";

/**
 * Pozicování vůči vedlejší ose
 * @see https://css-tricks.com/almanac/properties/a/align-items/
 * */
export enum AlignItems {
    initial = "initial",
    inherit = "inherit",
    baseline = "baseline",
    center = "center",
    flexEnd = "flex-end",
    flexStart = "flex-start",
    stretch = "stretch",
}

/**
 * Používá se ve VirtualizedListu a InfiniteVirtualizedListu k vytvoření hlavičky a patičky.
 *
 * Jelikož má mít hlavička a patička plnou šířku komponenty VirtualizedList, je potřeba jí předat šířku z Autosizeru.
 *
 * @param width Šířka vytvářené komponenty (hlavičky nebo patičky).
 */
export type VirtualizedListStickyComponentCreator = (width: number) => ReactNode;

/**
 * Určuje variantu stylů DataGridu. Variant je sdílený a předává se do InfiniteVirtualizedListu.
 */
export enum DataGridVariant {
    default = "default",
    gridParticipants = "grid-participants",
}

/** Typy fází animace */
export enum AnimationPhase {
    start = "start",
    end = "end",
}

/** Výčet velikostí komponent */
export enum ComponentSize {
    large = "large",
    small = "small",
}

/** Výčet podporovaných barev pozadí pro formulářové komponenty */
export enum BackgroundColor {
    yellow = "yellow",
    orange = "orange",
    blue = "blue",
}

/** Sdílené props event handlerů pro formulářové komponenty */
export type FormComponentEventHandlersProps = Readonly<{
    /** Callback volaný při zaměření pole */
    onFocus?: FocusEventHandler;
    /** Callback volaný při opuštění pole */
    onBlur?: FocusEventHandler;
    /** Callback volaný po kliknutí primárním tlačítkem myši na pole */
    onClick?: MouseEventHandler;
    /** Callback volaný po kliknutí jiným než primárním tlačítkem myši na pole */
    onAuxClick?: MouseEventHandler;
    /** Callback volaný po zmáčknutí myši v poli */
    onMouseDown?: MouseEventHandler;
    /** Callback volaný po přejetí myší na pole */
    onMouseOver?: MouseEventHandler;
    /** Callback volaný po vstupu myši do oblasti pole */
    onMouseEnter?: MouseEventHandler;
    /** Callback volaný po opuštění oblasti pole myší */
    onMouseLeave?: MouseEventHandler;
    /** Callback volaná po stisknutí tlačítka klávesnice */
    onKeyDown?: KeyboardEventHandler;
}>;

/** Props společné pro obecné formulářové komponenty použitelné i mimo MultiWeb. */
export type FormComponentProps = Readonly<{
    /** Unikátní identifikátor ovládacího prvku formuláře */
    id?: string;
    /** Příznak, zda je pole zakázáno. V takovém případě je pole neklikatelné a neemituje žádné eventy.
     * `disabled` se používá pouze tehdy, pokud komponenta neobsahuje atribut `readOnly`.
     */
    disabled?: boolean;
    /** Příznak, zda je hodnota pole nevalidní */
    invalid?: boolean;
    /** Příznak, zda je pole povinné */
    required?: boolean;
    /** Barva pozadí vstupního pole */
    backgroundColor?: BackgroundColor;
    /** Příznak, zda se má pole zobrazit jako šifrované. */
    encrypted?: boolean;
    /**
     * Příznak, zda je pole označeno pouze pro čtení což znamená, že je pole neměnitelné, ale použitlelné k označení nebo kopírování textu.
     * Tento atribut je preferovaný před atributem `disabled`, pokud ho komponenta obsahuje.
     * `readOnly` se nastavuje v souvislosti s přiznakem `enabledVisual` a reflektuje opak jeho hodnoty.
     * V rámci multiwebu zastává roli `disabled` (využívá stejné styly, aby se tvářil jako disabled) s tím rozdílem, že je využita jeho vlastnosti klikatelnosti.
     *
     * Pokud má uživatel právo editace, tak při kliknutí na `readOnly` komponentu zároveň dojde k přepnutí stavu editace formuláře na `isEditing=true` viz {@link EditableControlComponentProps}.
     *
     * @example (počítá s tim, že má uživatel potřebné právo pro editaci).
     * <HTMLElement readOnly=true isEditing=false /> - uživatel nemůže editovat.
     * Kliknu na element.
     * <HTMLElement readOnly=false isEditing=true /> - uživatel se přepíná do stavu isEditing a může editovat.
     */
    readOnly?: boolean;
    /** Příznak, zda se má komponenta použít v transparentním módu. Týká se to např. nastavení barvy pozadí, nezobrazování okrajů apod. */
    transparent?: boolean;
}>;

/**
 * Props předávané do základních komponent, které rozšiřují třídu GenericControl a jsou použitelné napříč formulářovými komponentami specifickými pro MultiWeb aplikaci.
 * Obsahuje pouze props odpovídající atributům z třídy GenericControl, které ovlivňují vykreslení základních komponent.
 */
export type GenericControlComponentProps = NoneOwnProps;

/**
 * Props odpovídající atributům vyskytujících se v základním needitačním control, které rozšiřují třídu Control a jsou použitelné napříč formulářovými komponentami specifickými pro MultiWeb aplikaci.
 * Obsahuje pouze props odpovídající atributům z třídy Control, které ovlivňují vykreslení základních needitačních komponent.
 *
 * Typ PropsWithChildren se používá kvůli chybě související s {@link https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-4.html#weak-type-detection|Weak type check}.
 */
export type ControlComponentProps = Readonly<PropsWithChildren<{
    /** Unikátní identifikátor komponenty. Id je určeno primárně pro input element v komponentě. */
    id?: string;
    /** Příznak zda je komponenta interaktivní */
    interactive?: boolean;
    /** Lokalizační klíč názvu komponenty a nápovědy */
    locKey: string;
    /** Lokalizační klíč pro text placeholderu */
    placeholderLocKey?: string;
    /** Příznak, zda je pole viditelné */
    visible?: boolean;
    /** Příznak, zda je pole povolené */
    enabled?: boolean;
    /** Příznak, zda je pole šifrováno */
    encrypted?: boolean;
    /** Hodnota komponenty */
    value?: unknown ;
}>> & GenericControlComponentProps;

/**
 * Callback určený na provolání při spuštění validace konkrétního control.
 */
export type OnValidationCallback = () => void;

/**
 * Callback určený na provolání při změně validačních chyb konkrétního control. Tj.
 * - buď při změně redux stavu `errors` v controls-modulu
 * - nebo při změně externích validačních chyb
 *
 * (Dá se použít pro napojení eventy `validityChanged` z původní/nereactí aplikace.)
 */
export type OnValidityChanged = () => void;

/**
 * Textová reprezentace controlu - typicky určená jako součást nadpisu formuláře (`DetailFrame._captionAttrs`).
 * - Pokud je grafická reprezentace controlu textová (např. Edit, NumEdit, ComboBox, ...), pak tu nejspíš bude 1:1, co v grafické reprezentaci.
 * - V ostatních případech (např. CheckBox) ...
 */
export type ControlDisplayedValue = string | null;

/**
 * Kompletní/naparsovaná hodnota. Nebo `undefined`, pokud rawValue není naparsovatelná. <br/>
 * V této formě jsou data ukládána např. v `DetailFrame.actualObject`.
 *
 * -
 *
 * Příklady pro `DateEdit`:
 * - pro `DateEdit` vždy vrací `Date | null | undefined`.
 * - pokud uživatel zadá - za rawValue - řetězec `"3.5.2004"`, pak tomu bude odpovídat `controlValue === new Date(2004, 5, 3)`.
 * - pokud uživatel zadá - za rawValue - `null` (nebo prázdný řetězec), pak tomu bude odpovídat `controlValue === null`.
 * - pokud uživatel zadá - za rawValue - řetězec `"nesmysl"`, pak tomu bude odpovídat `controlValue === undefined`.
 */
export type ControlValue = any;

/**
 * Callback volaný při mountu editovatelného control.
 * - Používá se pro předání věcí - specifických pro danou komponentu - navenek.
 * - Konkrétně pro předání převodníků mezi různými formami hodnoty controlu.
 * - Tyto převodníky využívá `EditableControlConnector` pro naplnění kontraktu některých svých metod (`displayedValue`, `controlValue`).
 */
export type OnEditableControlMount = (args: {
    /** Převádí rawValue na {@link ControlDisplayedValue}. */
    getDisplayedValue: (rawValue: ControlReduxRawValue) => ControlDisplayedValue;
    /** Převádí rawValue na {@link ControlValue}. */
    getControlValue: (rawValue: ControlReduxRawValue) => ControlValue;
    /** Převádí {@link ControlValue} na rawValue. */
    convertControlValueToRawValue: (controlValue: ControlValue) => ControlReduxRawValue;
}) => void;

/**
 * Props odpovídající atributům vyskytujících se v editačním control, které rozšiřují třídu EditableControl a jsou použitelné napříč formulářovými komponentami specifickými pro MultiWeb aplikaci.
 * Obsahuje pouze props odpovídající atributům z třídy EditableControl, které ovlivňují vykreslení editačních komponent.
 * Více informací v souboru `UI.React/components/formComponents.md`
 */
export type EditableControlComponentProps = Readonly<{
    /** Klíč sloužící k identifikaci pole v reduxu */
    dataKey: ControlsKey;
    /** Příznak, zda je pole odloženě povinné */
    postponedRequired?: boolean;
    /** Příznak, zda je hodnota pole pouze pro čtení */
    enabledVisual?: boolean;
    /** Příznak, zda je pole doporučené */
    recommended?: boolean;
    /** Příznak, zda je pole povinné */
    required?: boolean;
    /** Externí validační chyby. Tj. důvody nevalidnosti, které neřeší dané control. (Control je pouze dostane zvenku k zobrazení.) */
    externalErrors: ReadonlyArray<IExplicitValidationError>;
    /** True, pokud se má v tooltipu zobrazit hláška, že hodnota je povinná, ale není vyplněna (platnou) hodnotou. */
    isInRequiredInvalidState: boolean;
    /** Voláno při každém spuštění validace daným controlem. */
    onValidationCallback: OnValidationCallback;
    /** Voláno pouze, pokud se výsledek validace změnil. */
    onValidityChanged: OnValidityChanged;
    /** Reference na vstupni pole. */
    ref?: RefObject<HTMLElement>;
    /** Callback volaný při mountu editovatelného control. */
    onMount?: OnEditableControlMount;
    /**
     * Callback volaný při každé změně hodnoty pole.
     * Reaguje na React `onChange` event, ne na JavaScript `change` event.
     * Mezi těmito událostmi {@link https://stackoverflow.com/questions/38256332/in-react-whats-the-difference-between-onchange-and-oninput|je rozdíl}.
     */
    onChange?: OnChangeHandler<ControlReduxRawValue>;
    /**
     * Callback volaný při opuštění pole se změněnou hodnotou.
     * Od {@link onChange} se liší tím, že se nevolá při každé změně hodnoty v control.
     */
    triggerValueChange?: () => void;
    /** Příznak, zda je pole v módu editace. Příznak je opakem `readOnly` z {@link FormComponentProps}.
     * Pokud má uživatel právo editace, tak přepínáním editačního módu zároveň dochází k přepínaní `readOnly` viz {@link FormComponentProps}.
     */
    isEditing?: boolean;
}> & ControlComponentProps & FormComponentEventHandlersProps & MultiEditProps;

export type LoadableEditableControlComponentProps = Readonly<{
    /** Informace o tom, zda probiha loading */
    isLoading: boolean;
}> & EditableControlComponentProps;

/**
 * Props sdílené mezi fake kontejnery pro hromadnou editaci. Jedná se o props předávané zvenčí, tvoří tak rozhraní pro komponenty vstupující do multiEditMixinu.
 */
export type FakeContainerProps = {
    /** Lokalizační klíč pro získání vysvětlivky pole (title) a názvu pole (pro přístupnost v případě, že pro komponentu neexistuje odpovídající label) */
    locKey: string;
} & Pick<FakeComponentProps, "disabled" | "required" | "postponedRequired" | "recommended" | "onClick" | "onKeyDown" | "isEditing" | "ariaLabelledBy">;

/**
 * Props sdílené mezi fake komponentami pro hromadnou editaci.
 */
export type FakeComponentProps = {
    /** Popisek pole*/
    label: string;
    /** Vysvětlivka pole */
    title: string;
    /** Vysvětlivka pro hodnotu hromadné editace */
    valueTitle: string;
    /** Příznak, zda je pole zakázáno */
    disabled?: boolean;
    /**
     * Příznak, zda je aktivní editace. Pokud není aktivní, pole vypadá jako zakázané, ale ve skutečnosti zakázané není, aby se při kliknutí na pole
     * zaslala událost nadřazenému formuláři, který editaci aktivuje.
     */
    isEditing?: boolean;
    /** Callback volaný při kliknutí. Disabled nemá na volání vliv. Vyhodnocení disabled je zodpovědností tvůrce callbacku
     * (typicky {@link MultiEditWrapper} nebo {@link MultiEditMixin}) */
    onClick?: MouseEventHandler;
    /** Callback volaný při stisku klávesy. Disabled nemá na volání vliv. Vyhodnocení disabled je zodpovědností tvůrce callbacku
     * (typicky {@link MultiEditWrapper} nebo {@link MultiEditMixin}) */
    onKeyDown?: KeyboardEventHandler;
    /**
     * Prop pro zadání atributu <code>aria-labelledby</code>. Poskytuje čitelnost fake komponentám pro čtečky.
     * Nelze použít hodnotu for z label elementu, protože ten už je svázaný s reálnou komponentou. Proto se typicky zadává id label elementu.
     * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-labelledby_attribute
     */
    ariaLabelledBy?: string;
    /** Právo povinnost */
    required?: boolean;
    /** Právo doporučenost */
    recommended?: boolean;
    /** Právo odložené povinnosti */
    postponedRequired?: boolean;
};

/**
 * Props sdílené mezi needitovatelnymi fake komponentami pro hromadnou editaci.
 */
export type NoEditFakeComponentProps = {
    /** Vysvětlivka pole */
    title: string;
    /** Vysvětlivka pro hodnotu hromadné editace */
    valueTitle: string;
    /** Zobrazená hodnota */
    value: string;
};

/**
 * Props related to modal window size adjustment.
 */
export type ResizeOptionsProps = {
    /** Switch that determines whether the modal window can be resized (symmetrically) */
    readonly resizable?: boolean;
    /** Switch that determines whether the modal window can be resized (asymmetrically) */
    readonly asymmetricallyResizable?: boolean;
    /** Sets box width */
    readonly defaultWidth: number;
    /** Sets box height */
    readonly defaultHeight: number;
    /** Sets min box width */
    readonly minWidth?: number;
    /** Sets max box width */
    readonly maxWidth?: number;
    /** Sets min box height */
    readonly minHeight?: number;
    /** Sets max box height */
    readonly maxHeight?: number;
};

/**
 * Rozhraní pro reactové komponenty podporující hromadnou editaci.
 * Vychází z rozhraní IMultiEdit v ControlInterfaces.ts. ControlInterfaces ale nejsou striktní, proto je nelze využít přímo
 * v reactu.
 */
export type MultiEditProps = {
    /** Příznak značící, jestli je hromadná editace právě aktivní */
    multiEditActive: boolean;
    /** Příznak, zda proběhla interakce s fake komponentou v rámci jednoho cyklu hromadné editace. */
    touched: boolean;
};

/**
 * Dílčí props, které také vrací `EditableControlConnector._getRenderProps`. Jde o props získané z datových atributů kořenového elementu.
 * - Tyto props nejsou zcela společné.
 * - Nicméně se předávají do všech React komponent renderovaných přes `EditableControlConnector`.
 * - I do těch, které dané props nepodporují.
 * - Je to kvůli usnadnění implementace `EditableControlConnector._getRenderProps()`.
 *
 * Možnosti:
 * 1. Pro každou komponentu udělat konkrétní konektor. Např. pro `Edit` `EditConnector`.
 * Konkrétní konektor by tak předal přesně ty props, které vyžaduje konkrétní komponenta. Nic víc.
 * 2. **(Zvolené řešení)** Ponechat univerzální konektor `EditableControlConnector` použitelný pro vícero komponent.
 * A do těch komponent předávat všechny props z datových atributů. Pro jistotu, kdyby je náhodou komponenty potřebovaly.
 * 3. Ponechat univerzální konektor, ale při jeho instanciaci předat funkci, která z data atributů vyextrahuje
 * specifické props šité na míru pro konkrétní komponentu.
 * 4. Podobně jako 3. možnost. S tím, že funkce na extrakci specifických props by byla součástí komponenty - jako statická. Např. `Edit.extractPropsFromDataAttributes`.
 * Do konektoru by tak stačilo předat jen tu komponentu a konektor by si z ní  provolal `extractPropsFromDataAttributes`.
 *
 * Důvod:
 * - Rozhodli jsme se pro možnost 2: univerzální `EditableControlConnector` a univerzální props.
 * - Variantu 3 jsme rovnou zavrhli, protože by hrozilo riziko, že při instanciaci konektoru se předá špatná funkce na extrakci specifických props.
 * Je třeba mít robustní řešení.
 * - Varianta 4 je robustní. Ale vyžadovala by nestandardní implementace komponent. To by člověka mohlo mást.
 * - Ad možnost 1) Konkrétní konektory sice mají tu výhodu, že jsou šité na míru. A může tak být kód přehlednější.
 * To, co se do nich předává, to skutečně používají.
 * Také nemůže nastat kolize props. (Např. kdyby se přes datový atribut předávala prop `xyz` s nějakým významem/typem, ale daná komponenta očekávala také prop `xyz`, ale s jiným významem/typem.)
 * - Toto riziko jsme však vyhodnotili, že nehrozí.
 * - Přišlo nám rozumnější mít jednodušší implementaci - tj. jediný univerzální konektor. (I za cenu univerzálních props.)
 * - (Pozn. Možná i tak někdy bude třeba udělat specifický konektor. Ale z jiných důvodů. Např. proto, že je třeba, aby implementoval i jiná rozhraní.)
 */
export type DataAttributesProps = {
    /** Pokud zadáno, nebude možné zadat text delší než tato hodnota. */
    maxLength?: number;
    /** Pokud zadáno, nebude možné zadat text kratší než tato hodnota. */
    minLength?: number;
    /** Pokud zadáno, pak editovaný text nevyhovující tomuto regulárnímu výrazu bude nevalidní. */
    validationRegex?: string;
    /** Formátovací řetězec pro zobrazení hodnot čísel, datumů, bool hodnot a dalších. */
    displayFormat?: string;
    /** Lokalizační klíč pro popisek a nápovědný text ovládacího prvku. */
    captionLocKey?: string;
};

/**
 * Props specifické pro {@link CheckBox} získané z datových atributů kořenového elementu
 */
export type CheckBoxControlDataAttributesProps = {
    /** Určuje umístění popisu vůči CheckBoxu */
    alignment?: CaptionAlignment;
};

/**
 * Props pro komponenty, u kterých lze nastavovat hodnotu kurzoru.
 */
export type CursorValueAdjustableComponentProps = {
    /** Hodnota kurzoru předávaná do komponenty */
    cursor: Cursor;
};

/**
 * Typ přidávající vlastnosti, které se používají v případě, kdy má atribut přidanou vlastnost BackgroundAttr.
 */
export type BackgroundAttributeProps = Readonly<{
    /**
     * Určuje zda je barva atributu nastavena jiným atributem.
     * Pokud má atribut nastavenou vlastnost `BackkgrounAttrName`, hodnota v ní uvedeného atributu určuje barvu tohoto atributu.
     * V takovém případě je atribut obalen HTML kontejnerem, který udává jeho barvu.
     * Komponenty pak tuto barvu mohou využít pomocí CSS proměnné.
     * Např. Ikona pro barvu textu nebo <Badge/> pro barvu pozadí
    */
    hasBackgroundAttr?: boolean;
}>;

/**
 * Props společné pro NumEdit komponentu.
 */
export type NumEditComponentProps = Readonly<{
    /**
     * Omezuje horní limit číselné hodnoty atributu.
     */
    maxValue?: number;

    /**
     * Omezuje dolní limit číselné hodnoty atributu.
     */
    minValue?: number;

    /**
     * Formátovací řetězec pro zobrazení hodnot čísel, datumů, bool hodnot a dalších.
     */
    displayFormat?: string;

    /**
     * Datový typ atributu.
     */
    dataType?: OWDataType | string;
}>;

export type ComboBoxControlDataAttributesProps = Readonly<{
    /**
     * Jméno reduxového enumu, který bude nahrán
     */
    enumName: string;

    /**
     * Řetězec hodnot comboboxu oddělených středníkem, které nemají být zobrazeny v comboboxu
     */
    hideValues?: string;

    /**
     * Lokalizační klíč pro placeholder comboboxu
     */
    placeholderLocKey?: string;
}>;
